/**
 * /dashboard/posh/compliance — Org-wide POSH compliance roster
 * (HR-admin only).
 *
 * The backend is the boundary: GET /api/v1/posh/training/compliance is
 * RequireRole(company_admin, hr_manager) server-side; this UI is a
 * defence-in-depth mirror.
 *
 * Migration note (2026-06-01, Next.js → Vite, Phase 3d): port of
 * frontend/src/app/(dashboard)/dashboard/posh/compliance/page.tsx.
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  BellRing,
  ClipboardCheck,
  Download,
  Loader2,
  Search,
  ShieldX,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import type {
  POSHComplianceRow,
  POSHComplianceStatus,
} from "@/types/posh";
import { toApiError } from "@/lib/api";
import {
  is2FARequiredError,
  useCompliance,
  useRemindTraining,
} from "@/hooks/use-posh";
import type { RemindTrainingResponse } from "@/types/posh";
import { fmtDate, fmtDateTime } from "@/lib/posh-format";
import {
  moduleCertificatesCount,
  overallCertifiedLabel,
} from "@/lib/posh-employee";
import { toCsv } from "@/lib/csv";
import { policyAckLabel } from "@/lib/posh-policy-ack";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TrainingRefresherCard } from "@/components/posh/training-refresher-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/compliance",
)({
  component: ComplianceRoute,
});

const CAN_VIEW_COMPLIANCE = new Set<Role>([
  "company_admin",
  "hr_manager",
]);

// "refresher_due" is a view over in_progress rows whose training lapsed
// under the annual refresher (row.refresher_due) — not a server status.
type StatusFilter = "all" | POSHComplianceStatus | "refresher_due";

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "refresher_due", label: "Refresher Due" },
  { value: "compliant", label: "Compliant" },
];

const REFRESHER_DUE_BADGE = {
  tone: "warning" as const,
  label: "Refresher Due",
};

function isRefresherDue(r: POSHComplianceRow): boolean {
  return r.status !== "compliant" && !!r.refresher_due;
}

// The badge a row shows: an expired employee is in_progress server-side
// (pending) but the reason matters — they were trained and lapsed.
function rowBadge(r: POSHComplianceRow) {
  if (isRefresherDue(r)) return REFRESHER_DUE_BADGE;
  return STATUS_BADGE[r.status];
}

// "Certified until": for a refresher-due employee the honest answer is
// that it already lapsed (the server's date is then the next OTHER
// module's lapse, which would read as still certified).
// 2026-10 persona round: certificates are issued PER MODULE, so a date
// shows only when the person is compliant overall (the server sends none
// otherwise) — never "Certified until 2027" next to "In progress 1/6".
function certifiedUntilLabel(r: POSHComplianceRow): string {
  if (isRefresherDue(r)) return "Lapsed";
  return overallCertifiedLabel(r.status, r.certified_until, (iso) => fmtDate(iso));
}

const STATUS_BADGE: Record<
  POSHComplianceStatus,
  { tone: "neutral" | "progress" | "success" | "warning"; label: string }
> = {
  not_started: { tone: "neutral", label: "Not Started" },
  in_progress: { tone: "progress", label: "In Progress" },
  compliant: { tone: "success", label: "Compliant" },
};

const STATUS_RANK: Record<POSHComplianceStatus, number> = {
  not_started: 0,
  in_progress: 1,
  compliant: 2,
};

function NoAccess() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <PoshModuleShell>
      <PageHeader
        title="Overview"
        description="Org-wide training & certification status."
      />
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
            <ShieldX className="h-6 w-6" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-medium text-gray-900">
              You don&apos;t have access
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can view the org-wide
              POSH compliance roster. Ask an admin on your team, or head
              back to POSH.
            </p>
          </div>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back to POSH
            </Link>
          </Button>
        </CardContent>
      </Card>
    </PoshModuleShell>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
  hint,
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "muted" | "warning";
  hint?: string;
}) {
  const valueTone: Record<typeof tone, string> = {
    default: "text-primary",
    success: "text-green-700",
    muted: "text-muted-foreground",
    warning: "text-amber-700",
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs font-medium uppercase tracking-wide">
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p
          className={`text-3xl font-semibold tabular-nums ${valueTone[tone]}`}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
}

function RosterSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="space-y-3 py-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ProgressCell({ row }: { row: POSHComplianceRow }) {
  const total = row.mandatory_total;
  const pct =
    total > 0
      ? Math.min(100, Math.round((row.completed / total) * 100))
      : 0;
  return (
    <div className="min-w-[8rem] space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="tabular-nums">
          Overall: {row.completed} of {total} modules
        </span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <Progress
        value={pct}
        className="h-1.5"
        aria-label={`${row.completed} of ${total} mandatory modules completed`}
      />
      {row.module_certificates !== undefined && (
        <p className="text-xs text-muted-foreground">
          {moduleCertificatesCount(row.module_certificates)}
        </p>
      )}
    </div>
  );
}

function Roster() {
  const q = useCompliance();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const remind = useRemindTraining();

  const allRows = useMemo(() => q.data?.rows ?? [], [q.data]);

  // Proper-plural success toast: "Reminded 3 employees · 1 skipped
  // (recently reminded)".
  function toastRemindResult(res: RemindTrainingResponse) {
    const emp = res.reminded === 1 ? "employee" : "employees";
    const skipped =
      res.skipped > 0 ? ` · ${res.skipped} skipped (recently reminded)` : "";
    toast.success(`Reminded ${res.reminded} ${emp}${skipped}`);
  }

  function remindOne(userId: number) {
    remind.mutate(
      { user_id: userId },
      {
        onSuccess: toastRemindResult,
        onError: (err) =>
          toast.error(
            toApiError(err).message || "Could not send the reminder.",
          ),
      },
    );
  }

  function remindAll() {
    remind.mutate(
      { all: true },
      {
        onSuccess: toastRemindResult,
        onError: (err) =>
          toast.error(
            toApiError(err).message || "Could not send the reminders.",
          ),
      },
    );
  }

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      if (status === "refresher_due") {
        if (!isRefresherDue(r)) return false;
      } else if (status !== "all" && r.status !== status) return false;
      if (!needle) return true;
      return (
        r.name.toLowerCase().includes(needle) ||
        r.email.toLowerCase().includes(needle)
      );
    });
    return [...filtered].sort((a, b) => {
      const d = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (d !== 0) return d;
      return a.name.localeCompare(b.name);
    });
  }, [allRows, search, status]);

  if (q.isLoading) return <RosterSkeleton />;

  const apiErr = q.isError ? toApiError(q.error) : null;
  if (
    apiErr &&
    ((q.error as { response?: { status?: number } })?.response?.status ===
      403 ||
      is2FARequiredError(q.error))
  ) {
    return (
      <Alert>
        <AlertTitle>You don&apos;t have access</AlertTitle>
        <AlertDescription>
          Your account can&apos;t view the org-wide POSH compliance
          roster. This is restricted to company admins and HR managers.
        </AlertDescription>
      </Alert>
    );
  }
  if (apiErr) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load compliance</AlertTitle>
        <AlertDescription>{apiErr.message}</AlertDescription>
      </Alert>
    );
  }

  const summary = q.data?.summary;
  const nonCompliantCount = allRows.filter(
    (r) => r.status !== "compliant",
  ).length;

  function exportCsv() {
    if (rows.length === 0) return;
    const header = [
      "Name",
      "Email",
      "Department",
      "Completed",
      "Mandatory total",
      "Module certificates",
      "Status",
      "Fully certified until",
      "Last activity",
      "Policy acknowledged",
    ];
    const body = rows.map((r) => [
      r.name,
      r.email,
      r.department ?? "",
      String(r.completed),
      String(r.mandatory_total),
      r.module_certificates !== undefined ? String(r.module_certificates) : "",
      rowBadge(r).label,
      r.status === "compliant" || isRefresherDue(r) ? certifiedUntilLabel(r).replace("—", "") : "",
      r.last_activity_at ? fmtDateTime(r.last_activity_at) : "",
      policyAckLabel(r.policy_acknowledged_at, fmtDate),
    ]);
    const blob = new Blob([toCsv([header, ...body])], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `posh-compliance-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(
      `Exported ${rows.length} ${
        rows.length === 1 ? "employee" : "employees"
      } to CSV.`,
    );
  }

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          <StatCard
            label="Total Employees"
            value={summary.total_employees}
          />
          <StatCard
            label="Compliant"
            value={summary.compliant}
            tone="success"
          />
          <StatCard
            label="In Progress"
            value={summary.in_progress}
          />
          {summary.refresher_due !== undefined && (
            <StatCard
              label="Refresher Due"
              value={summary.refresher_due}
              tone="warning"
              hint="Trained before, lapsed — counted as in progress"
            />
          )}
          <StatCard
            label="Not Started"
            value={summary.not_started}
            tone="muted"
          />
          <StatCard
            label="Lapsing ≤30d"
            value={summary.cert_expiring_soon}
            tone="warning"
            hint="Refresher falls due within 30 days"
          />
          <StatCard
            label="Mandatory Modules"
            value={summary.mandatory_modules}
            tone="muted"
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Employee Roster
              </CardTitle>
              <CardDescription>
                Who has completed mandatory POSH training, whose training
                is about to lapse, and who owes a refresher. Non-compliant
                employees are listed first.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {/* Bulk reminder — emails people, so it confirms first. */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1.5"
                    disabled={nonCompliantCount === 0 || remind.isPending}
                  >
                    {remind.isPending && remind.variables?.all ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <BellRing className="h-4 w-4" />
                    )}
                    Remind All Non-Compliant
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Email {nonCompliantCount} non-compliant{" "}
                      {nonCompliantCount === 1 ? "employee" : "employees"}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Each will get a reminder to finish their mandatory
                      POSH training. Anyone already reminded in the last
                      24 hours is skipped automatically.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remindAll}>
                      Send Reminders
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button
                type="button"
                variant="outline"
                className="gap-1.5"
                onClick={exportCsv}
                disabled={rows.length === 0}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="roster-search">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="roster-search"
                  placeholder="Name or email…"
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5 sm:w-56">
              <Label htmlFor="roster-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as StatusFilter)}
              >
                <SelectTrigger id="roster-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {allRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No employees yet. Add your team under People, then they
              appear here as they work through mandatory POSH training.
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No employees match your search or filter.
            </div>
          ) : (
            <>
            {/* Phones: a card per person — name, status, progress and
                Remind in view (the 9-column table left only Name + Email
                on screen, Remind ~900 px to the right). */}
            <ul className="-mx-2 divide-y sm:hidden">
              {rows.map((r) => {
                const badge = rowBadge(r);
                const rowPending =
                  remind.isPending && remind.variables?.user_id === r.user_id;
                return (
                  <li key={r.user_id} className="space-y-2.5 px-2 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="break-words font-medium text-gray-900">
                          {r.name}
                        </p>
                        <p
                          className="truncate text-xs text-muted-foreground"
                          title={r.department?.trim() ? `${r.department} · ${r.email}` : r.email}
                        >
                          {r.department?.trim()
                            ? `${r.department} · ${r.email}`
                            : r.email}
                        </p>
                      </div>
                      <StatusBadge
                        tone={badge.tone}
                        label={badge.label}
                        className="shrink-0"
                      />
                    </div>
                    <ProgressCell row={r} />
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="min-w-0">
                        <dt className="text-muted-foreground">Certified Until</dt>
                        <dd>{certifiedUntilLabel(r)}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-muted-foreground">Policy</dt>
                        <dd>{policyAckLabel(r.policy_acknowledged_at, fmtDate)}</dd>
                      </div>
                    </dl>
                    {r.status !== "compliant" && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-1.5 [@media(pointer:coarse)]:h-11"
                        disabled={remind.isPending}
                        onClick={() => remindOne(r.user_id)}
                        aria-label={`Remind ${r.name}`}
                      >
                        {rowPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BellRing className="h-4 w-4" />
                        )}
                        Remind
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="hidden overflow-x-auto sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Fully Certified Until</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Policy Acknowledged</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const badge = rowBadge(r);
                    const rowPending =
                      remind.isPending &&
                      remind.variables?.user_id === r.user_id;
                    return (
                      <TableRow key={r.user_id}>
                        <TableCell className="font-medium text-gray-900">
                          {r.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.email}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.department?.trim() ? r.department : "—"}
                        </TableCell>
                        <TableCell>
                          <ProgressCell row={r} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            tone={badge.tone}
                            label={badge.label}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {certifiedUntilLabel(r)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {fmtDateTime(r.last_activity_at)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {r.policy_acknowledged_at ? (
                            <StatusBadge
                              tone="success"
                              label={policyAckLabel(r.policy_acknowledged_at, fmtDate)}
                            />
                          ) : (
                            <span className="text-muted-foreground">
                              {policyAckLabel(null, fmtDate)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.status !== "compliant" && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5"
                              disabled={remind.isPending}
                              onClick={() => remindOne(r.user_id)}
                            >
                              {rowPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <BellRing className="h-3.5 w-3.5" />
                              )}
                              Remind
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </>
          )}

          {allRows.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {rows.length} of {allRows.length}{" "}
              {allRows.length === 1 ? "employee" : "employees"}.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ComplianceRoute() {
  const { user } = useAuth();
  const role = user?.role;
  const allowed = !!role && CAN_VIEW_COMPLIANCE.has(role);

  if (!allowed) return <NoAccess />;

  return (
    <PoshModuleShell
      title="Overview"
      description="Org-wide POSH training & certification status — the actionable roster for your statutory records. Export it for the auditor."
    >
      <PoshFeatureGate>
        <div className="space-y-6">
          <Roster />
          <TrainingRefresherCard />
        </div>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
